import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
	StickyNote,
	Phone,
	Mail,
	CalendarDays,
	MessageCircle,
	Linkedin,
	MessageSquare,
	CheckSquare,
	Loader2,
	Trash2,
	Send
} from 'lucide-react';
import {
	Button,
	Textarea,
	Input,
	Card,
	CardContent,
	Select,
	SelectTrigger,
	SelectValue,
	SelectContent,
	SelectItem,
	useToast
} from '@hiring-intelligence/ui';
import { ACTIVITY_TYPE_OPTIONS } from '@hiring-intelligence/shared';
import { crmActivitiesApi } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import { format } from 'date-fns';

const ACTIVITY_ICONS: Record<string, typeof StickyNote> = {
	note: StickyNote,
	call: Phone,
	email: Mail,
	meeting: CalendarDays,
	whatsapp: MessageCircle,
	linkedin: Linkedin,
	sms: MessageSquare,
	task: CheckSquare
};

const ACTIVITY_LABELS: Record<string, string> = Object.fromEntries(
	ACTIVITY_TYPE_OPTIONS.map((o) => [o.value, o.label])
);

interface ActivityLogProps {
	contactId?: string;
	companyId?: string;
	dealId?: string;
}

export interface ActivityLogHandle {
	/** Select an activity type in the composer and focus the note field — driven by the quick-action bar. */
	startActivity: (type: string) => void;
}

export const ActivityLog = forwardRef<ActivityLogHandle, ActivityLogProps>(function ActivityLog(
	{ contactId, companyId, dealId },
	ref
) {
	const queryClient = useQueryClient();
	const { toast } = useToast();
	const currentUser = useAuthStore((s) => s.user);

	const [type, setType] = useState<string>('note');
	const [subject, setSubject] = useState('');
	const [body, setBody] = useState('');
	const bodyRef = useRef<HTMLTextAreaElement>(null);

	useImperativeHandle(ref, () => ({
		startActivity: (next: string) => {
			setType(next);
			// focus the note field without yanking page scroll — the caller handles smooth-scroll
			requestAnimationFrame(() => bodyRef.current?.focus({ preventScroll: true }));
		}
	}), []);

	// Stable query key for this entity scope
	const scopeKey = contactId
		? ['crm-activities', 'contact', contactId]
		: companyId
			? ['crm-activities', 'company', companyId]
			: ['crm-activities', 'deal', dealId];

	const { data, isLoading } = useQuery({
		queryKey: scopeKey,
		queryFn: () => crmActivitiesApi.list({ contactId, companyId, dealId, limit: 100 }),
		enabled: !!(contactId || companyId || dealId)
	});

	const createMutation = useMutation({
		mutationFn: () =>
			crmActivitiesApi.create({
				type,
				subject: subject.trim() || undefined,
				body: body.trim() || undefined,
				contactId,
				companyId,
				dealId
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: scopeKey });
			setType('note');
			setSubject('');
			setBody('');
			toast({ title: 'Activity logged' });
		},
		onError: (error: Error) => {
			toast({ title: 'Error', description: error.message, variant: 'destructive' });
		}
	});

	const deleteMutation = useMutation({
		mutationFn: (id: string) => crmActivitiesApi.remove(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: scopeKey });
			toast({ title: 'Activity deleted' });
		},
		onError: (error: Error) => {
			toast({ title: 'Error', description: error.message, variant: 'destructive' });
		}
	});

	const handleSubmit = () => {
		if (!body.trim() && !subject.trim()) {
			toast({ title: 'Empty activity', description: 'Add a subject or note before logging.', variant: 'destructive' });
			return;
		}
		createMutation.mutate();
	};

	const activities = data?.data || [];

	return (
		<div className="space-y-4">
			{/* Composer */}
			<Card>
				<CardContent className="pt-6 space-y-3">
					<Select value={type} onValueChange={setType}>
						<SelectTrigger>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{ACTIVITY_TYPE_OPTIONS.map((o) => (
								<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
							))}
						</SelectContent>
					</Select>
					<Input
						placeholder="Subject (optional)"
						value={subject}
						onChange={(e) => setSubject(e.target.value)}
					/>
					<Textarea
						ref={bodyRef}
						placeholder="Add a note or log details of the interaction..."
						value={body}
						onChange={(e) => setBody(e.target.value)}
						rows={3}
						className="text-sm"
					/>
					<div className="flex justify-end">
						<Button size="sm" onClick={handleSubmit} disabled={createMutation.isPending}>
							{createMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
							Log Activity
						</Button>
					</div>
				</CardContent>
			</Card>

			{/* Timeline */}
			{isLoading ? (
				<div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
					<Loader2 className="h-4 w-4 animate-spin" /> Loading activities...
				</div>
			) : activities.length === 0 ? (
				<p className="text-sm text-muted-foreground text-center py-6">No activity logged yet.</p>
			) : (
				<div className="space-y-3">
					{activities.map((activity: any) => {
						const Icon = ACTIVITY_ICONS[activity.type] || StickyNote;
						const canDelete = currentUser?.role === 'super_admin' || activity.createdById === currentUser?.id;
						return (
							<div key={activity.id} className="flex gap-3 group">
								<div className="mt-0.5 p-2 rounded-full bg-muted shrink-0 h-9 w-9 flex items-center justify-center">
									<Icon className="h-4 w-4 text-muted-foreground" />
								</div>
								<div className="flex-1 min-w-0 border-b pb-3">
									<div className="flex items-start justify-between gap-2">
										<div className="min-w-0">
											<div className="flex items-center gap-2 flex-wrap">
												<span className="text-sm font-medium">
													{ACTIVITY_LABELS[activity.type] || activity.type}
												</span>
												{activity.subject && (
													<span className="text-sm text-foreground truncate">— {activity.subject}</span>
												)}
											</div>
											{activity.body && (
												<p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap break-words">{activity.body}</p>
											)}
											<div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
												<span>{activity.creator?.name || 'Unknown'}</span>
												<span>·</span>
												<span>{format(new Date(activity.occurredAt), 'd MMM yyyy, HH:mm')}</span>
											</div>
										</div>
										{canDelete && (
											<Button
												variant="ghost"
												size="icon"
												className="shrink-0 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
												onClick={() => {
													if (window.confirm('Delete this activity?')) deleteMutation.mutate(activity.id);
												}}
											>
												<Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
											</Button>
										)}
									</div>
								</div>
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
});

export default ActivityLog;
